import React from 'react';
import { useLoaderData, useNavigate } from '@tanstack/react-router';
import { SurveySettings } from '../../types';
import { postSubmission } from '../../api/submissions/post-submission';
import { getSurveyModel } from '../survey-questions/get-survey-model';
import Survey from '../survey/survey';
import { CurrentPageChangingEvent, SurveyModel } from 'survey-core';
import { useStatusMessageDispatch } from '../../hooks/StatusMessageContext';
import { isApiError } from '../../utils/errors';
import { extractCaptchaToken } from '../survey-questions/captcha/extract-captcha-token';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { setEmailSentTimestamp } from '../email-confirmation-message/email-confirmation-message';

const PROGRESS_BUTTONS_SELECTOR = '.sd-progress-buttons__list-container';
const NEXT_BUTTON_ID = 'sv-nav-next';

// Create a styled div to prevent cursor change on progressbar buttons
// This implies to the user that they can't click on the progressbar buttons to navigate
// Only applies to the start page, where we are enforcing users to use the NEXT button to create a submission
const StyledSurveyContainer = styled.div`
  ${PROGRESS_BUTTONS_SELECTOR} {
    pointer-events: none;
  }
`;

export interface SurveyStartLoaderData {
  settings: SurveySettings;
  metadataToken: string | undefined;
}

// First page of a survey application
const SurveyStart: React.FC = () => {
  const { settings, metadataToken } = useLoaderData({ from: '/$accountSlug/' }) as SurveyStartLoaderData;
  const { setStatusMessage } = useStatusMessageDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation('components', { keyPrefix: 'pages' });
  const accountSlug = settings.urlSlug;

  const handleSubmission = async (sender: SurveyModel, options: CurrentPageChangingEvent) => {
    options.allow = false;

    // Disable the next button while attempting to create a submission
    const nextButton = sender.navigationBar?.getActionById(NEXT_BUTTON_ID);
    nextButton.enabled = false;

    try {
      // Extract captcha token from the survey data
      const captchaToken = extractCaptchaToken(sender) || '';

      const submission = await postSubmission(accountSlug, sender.data, captchaToken, metadataToken);

      // Store timestamp in localStorage and start 30-second countdown
      setEmailSentTimestamp(submission.publicId);

      setStatusMessage({
        message: t('verification_email_sent'),
        severity: 'success',
      });

      navigate({ to: `/${accountSlug}/${submission.publicId}` });
    } catch (err) {
      console.error(err);
      setStatusMessage({
        message: isApiError(err) ? err.message : t('an_error_occurred'),
        severity: 'error',
      });

      // Re-enable the next button if the submission fails
      nextButton.enabled = true;
    }
  };

  const surveyModel = getSurveyModel({
    settings,
    onCurrentPageChanging: handleSubmission,
  });

  return (
    <StyledSurveyContainer>
      <Survey
        surveyModel={surveyModel}
      />
    </StyledSurveyContainer>
  );
};

export default SurveyStart;
